import { Link } from "react-router-dom";
import { MapPin, Trophy, Flag, Medal, Sparkles, Calendar, Newspaper, Bot } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ValidationShield } from "@/components/ValidationShield";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import { useAuth } from "@/lib/auth";

/**
 * @typedef FeatureReason
 * @property {string} text
 * @property {"medal"|"achievement"|"camp"|"event"|"activity"} kind
 * @property {string|null} [date_label]
 * @property {string|null} [medal]
 * @property {string|null} [source_url]
 */

/**
 * @param {{athlete: object, featureReason?: FeatureReason|null}} props
 */
export function AthleteCard({ athlete, featureReason }) {
  const { user } = useAuth();
  if (!athlete) return null;
  const isStaff = (user?.roles || []).some((r) => ["admin", "copf"].includes(r));
  const isAICreated = athlete.creation_source === "curator_ai" || (athlete.tags || []).includes("created_by_ai_from_article");
  const initials = (athlete.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const achievements = athlete.achievements?.length || 0;
  const photo = resolveAthletePhoto(athlete);
  const hasRealPhoto = !!athlete.photo_url;
  const isRoad2027 = !!athlete.road_to_2027 || !!athlete.road_to_2027_status;
  const livretAgree = !!athlete.livret_agree_copf;
  // V11.14 — priorité au feature_reason fourni par le backend (via prop),
  //          puis à celui embarqué dans l'objet athlete, sinon aucun.
  const reason = featureReason || athlete.feature_reason || null;
  return (
    <Link to={`/athletes/${athlete.slug || athlete.id}`} data-testid="athlete-card" className="group block">
      <Card className="overflow-hidden aito-card-hover h-full">
        <div className="relative aspect-[4/3] bg-[hsl(var(--aito-black))]">
          <img
            src={photo}
            alt={athlete.full_name}
            className={`absolute inset-0 h-full w-full ${hasRealPhoto ? "object-cover opacity-95" : "object-contain bg-white p-6"}`}
          />
          {hasRealPhoto && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
          )}
          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            <ValidationShield level={athlete.validation?.level || "declaratif"} />
            {isStaff && isAICreated && (
              <span
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-violet-500/90 text-white border border-violet-300 shadow-sm"
                title="Fiche créée automatiquement par l'IA depuis un article du Curator. À enrichir en priorité."
                data-testid="athlete-card-ai-badge"
              >
                <Bot className="h-2.5 w-2.5" />
                Créé par IA
              </span>
            )}
            {isRoad2027 && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-[hsl(var(--aito-gold))] text-black" title="Objectif déclaré par l'athlète, pas une sélection officielle">
                <Flag className="h-3 w-3" /> Objectif JP 2027
              </span>
            )}
            {livretAgree && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-black text-white border border-[hsl(var(--aito-gold))]">
                Livret agréé COPF
              </span>
            )}
          </div>
          <div className={`absolute inset-x-0 bottom-0 p-4 flex items-end gap-3 ${hasRealPhoto ? "text-white" : "text-foreground bg-background/90 backdrop-blur-sm"}`}>
            <Avatar className="h-12 w-12 border-2 border-white/40 shrink-0">
              <AvatarImage src={photo} />
              <AvatarFallback className="bg-[hsl(var(--aito-lagoon))] text-black font-display">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="font-display font-semibold text-base leading-tight line-clamp-2 sm:line-clamp-1 sm:truncate">
                {athlete.full_name}
              </div>
              {(athlete.main_discipline || (athlete.disciplines || []).length > 0) && (
                <div className={`text-xs mt-0.5 truncate ${hasRealPhoto ? "text-white/80" : "text-muted-foreground"}`}>{athlete.main_discipline || (athlete.disciplines || []).slice(0, 2).join(" · ")}</div>
              )}
            </div>
          </div>
        </div>
        {/* V11.14 — Bandeau "raison" (pourquoi cet athlète est à la UNE) */}
        {reason && reason.text && (
          <FeatureReasonBar reason={reason} />
        )}
        <div className="p-3 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-muted-foreground min-w-0 truncate">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{athlete.commune || athlete.island || "Polynésie"}</span>
          </span>
          {achievements > 0 && (
            <span className="flex items-center gap-1 font-mono text-foreground/80 shrink-0">
              <Trophy className="h-3.5 w-3.5" /> {achievements}
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}

// ============================================================================
// FeatureReasonBar : mini bandeau collé sous la photo (pourquoi cet athlète
// est à la UNE — médaille, achievement, stage, événement, activité).
// ============================================================================
function FeatureReasonBar({ reason }) {
  const cfg = {
    article: {
      Icon: Newspaper,
      bg: "bg-[hsl(var(--aito-lagoon))]/12 border-t-[hsl(var(--aito-lagoon))]/40 text-foreground",
      iconCls: "text-[hsl(var(--aito-lagoon))]",
      badge: "À LA UNE",
    },
    medal: {
      Icon: Medal,
      bg: "bg-[hsl(var(--aito-gold))]/15 border-t-[hsl(var(--aito-gold))]/40 text-foreground",
      iconCls:
        reason.medal === "or" ? "text-[hsl(var(--aito-gold))]" :
        reason.medal === "argent" ? "text-slate-400" :
        "text-orange-500",
      badge: "MÉDAILLE",
    },
    achievement: {
      Icon: Trophy,
      bg: "bg-[hsl(var(--aito-lagoon))]/10 border-t-[hsl(var(--aito-lagoon))]/40 text-foreground",
      iconCls: "text-[hsl(var(--aito-lagoon))]",
      badge: "RÉSULTAT",
    },
    camp: {
      Icon: Sparkles,
      bg: "bg-[hsl(var(--aito-lagoon-2))]/10 border-t-[hsl(var(--aito-lagoon-2))]/40 text-foreground",
      iconCls: "text-[hsl(var(--aito-lagoon-2))]",
      badge: "STAGE",
    },
    event: {
      Icon: Calendar,
      bg: "bg-[hsl(var(--aito-lagoon))]/10 border-t-[hsl(var(--aito-lagoon))]/40 text-foreground",
      iconCls: "text-[hsl(var(--aito-lagoon))]",
      badge: "ÉVÉNEMENT",
    },
    activity: {
      Icon: Sparkles,
      bg: "bg-muted/60 border-t-border text-foreground",
      iconCls: "text-muted-foreground",
      badge: "ACTIVITÉ",
    },
  }[reason.kind] || {
    Icon: Sparkles,
    bg: "bg-muted/60 border-t-border text-foreground",
    iconCls: "text-muted-foreground",
    badge: "À LA UNE",
  };
  const { Icon, bg, iconCls, badge } = cfg;
  return (
    <div
      className={`px-3 py-2 border-t text-[11px] leading-snug flex items-start gap-2 ${bg}`}
      data-testid="athlete-card-feature-reason"
    >
      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${iconCls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono text-[9px] font-bold tracking-wider uppercase px-1 py-0.5 rounded bg-background/80 border">
            {badge}
          </span>
          {reason.date_label && (
            <span className="text-[10px] font-mono text-muted-foreground">{reason.date_label}</span>
          )}
        </div>
        <div className="line-clamp-2 mt-0.5 font-medium" title={reason.text}>{reason.text}</div>
      </div>
    </div>
  );
}
